import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import Index from "./pages/Index";
import { UserProvider } from "./contexts/user/provider";
import { ErrorBoundary2 } from "./pages/ErrorBoundary2";
import { StatsProvider } from "./contexts/stats/provider";
import { AudioPlayer, AudioPlayerProvider } from "./components/AudioPlayer";
import { SpeechToTextProvider } from "./contexts/speechToText";
import { AuthenticatedRoute } from "./components/conditionalRoutes/AuthenticatedRoute";
import { GuestRoute } from "./components/conditionalRoutes/GuestRoute";
import { HomeScreenOnboardingHandler } from "./components/HomeScreenOnboardingHandler";
import { useAllowSelectionDrag } from "./hooks/useAllowSelectionDrag";

// Every route but the landing page is code-split, so the initial download stays
// small. The admin pages in particular drag in recharts, which no visitor to "/"
// needs.
const Register = lazy(() => import("./pages/Register"));
const Login = lazy(() => import("./pages/Login"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword").then(m => ({ default: m.ForgotPassword })));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword").then(m => ({ default: m.UpdatePassword })));
const ResetPasswordConfirmation = lazy(() => import("./pages/ResetPasswordConfirmation").then(m => ({ default: m.ResetPasswordConfirmation })));
const Dashboard = lazy(() => import("./pages/Dashboard").then(m => ({ default: m.Dashboard })));
const TaskEditor = lazy(() => import("./pages/TaskEditor").then(m => ({ default: m.TaskEditor })));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="flex items-center justify-center h-dvh">
    <Loader2 className="animate-spin" />
  </div>
);

const App = () => {
  useAllowSelectionDrag();

  return (
    <ErrorBoundary2 msg={"Root"}>
      <UserProvider>
        <StatsProvider>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <AudioPlayerProvider>
                <SpeechToTextProvider>
                  <Toaster />
                  <Sonner />
                  <div className="h-dvh flex flex-1 flex-col">
                    <div className="overflow-hidden">

                      <BrowserRouter>
                        {/* <Header /> */}
                        <Suspense fallback={<RouteFallback />}>
                          <Routes>
                            {/* Unrestricted routes */}
                            <Route path="/" element={<Index />} />
                            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

                            {/* Account routes */}
                            <Route path="/register" element={<GuestRoute children={<Register />} />} />
                            <Route path="/login" element={<GuestRoute children={<Login />} />} />
                            <Route path="/forgot-password" element={<GuestRoute children={<ForgotPassword />} />} />
                            <Route path="/update-password" element={<AuthenticatedRoute children={<UpdatePassword />} />} />
                            <Route path="/reset-password-confirmation" element={<ResetPasswordConfirmation />} />

                            {/* Admin routes */}
                            <Route path="/dashboard" element={<AuthenticatedRoute requiredPermissions={["admin.stats.read"]} children={<Dashboard />} />} />
                            <Route path="/task-editor" element={<AuthenticatedRoute requiredPermissions={["admin.stats.read"]} children={<TaskEditor />} />} />

                            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </Suspense>
                      </BrowserRouter>
                    </div>

                    <AudioPlayer
                      showSkipBackAndForward={false}
                    />
                    <HomeScreenOnboardingHandler />
                  </div>
                </SpeechToTextProvider>
              </AudioPlayerProvider>
            </TooltipProvider>
          </QueryClientProvider>
        </StatsProvider>
      </UserProvider>
    </ErrorBoundary2>
  );
};

export default App;